import { LightbulbFilament } from "@phosphor-icons/react";
import { Banner, Tab, TabList, TabPanel, TabProvider } from "@replicate/ui";
import {
  useMutation,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { isObject } from "lodash-es";
import { useState } from "react";
import type { FieldValues, UseFormReturn } from "react-hook-form";
import { FormProvider } from "react-hook-form";
import { Toaster } from "react-hot-toast";
import { useQueryParam } from "../../hooks";
import { getInputSchema } from "../../schema";
import type {
  AccessToken,
  CogInputSchema,
  Deployment,
  ErrorWithJSON,
  Features,
  Model,
  OpenapiSchema,
  PlaygroundPermissions,
  Prediction,
  Version,
} from "../../types";
import {
  createDeploymentPrediction,
  createModelPrediction,
  createVersionPrediction,
} from "./api";
import { TERMINAL_PREDICTION_STATUSES } from "./constants";
import {
  PlaygroundContext,
  usePlaygroundContext,
  type PlaygroundContextValue,
} from "./context";
import { FeaturedInputs } from "./featured-inputs";
import {
  queryKeys,
  useDockerImage,
  useInputsForm,
  usePrediction,
  useVersionedInputHints,
  useVersionlessInputHints,
} from "./hooks";
import { InitialOutput } from "./initial-output";
import { InputForm, type TextInputOptions } from "./input-form";
import { InputFormFooter } from "./input-form-footer";
import { InputOutputGrid } from "./input-output-grid";
import JSONEditor from "./json-editor";
import { PredictionDetailOutput } from "./prediction-detail-output";
import { PredictionErrorState } from "./prediction-error-state";
import { PredictionLoadingState } from "./prediction-loading-state";
import { RunWithAPIPlayground, RunWithContext } from "./run-with";
import { RunWithNPX } from "./run-with-npx";
import { SetupFailureCheck } from "./setup-failure-check";
import {
  cleanInputForSubmission,
  getElementVisibility,
  getRenderMode,
  processInputForSubmission,
} from "./util";

type InitialPrediction =
  | {
      usesVersionlessApi: false;
      prediction: Prediction;
      version: Version;
    }
  | {
      usesVersionlessApi: true;
      prediction: Prediction;
      version: Version | null;
    };

interface BaseAPIPlaygroundProps {
  deployment?: never;
  features: Features;
  hideAdvancedInputs?: boolean;
  initialPrediction: Prediction | null;
  initialPredictionVersion: Version | null;
  isAuthenticated: boolean;
  model?: never;
  modelInputSettings?: { hidden: string[] };
  modelStatus?: "online" | "offline";
  permissions: PlaygroundPermissions;
  setPredictionIdInUrl?: boolean;
  schema: OpenapiSchema;
  token?: AccessToken | null;
  version?: never;
}

type VersionAPIPlaygroundProps = Omit<BaseAPIPlaygroundProps, "version"> & {
  version: Version;
};

type ModelAPIPlaygroundProps = Omit<
  BaseAPIPlaygroundProps,
  "model" | "version"
> & {
  model: Model;
  version: Version;
};

type DeploymentAPIPlaygroundProps = Omit<
  BaseAPIPlaygroundProps,
  "deployment"
> & {
  deployment: Deployment;
};

export default function APIPlayground({
  deployment,
  features,
  hideAdvancedInputs,
  initialPrediction,
  initialPredictionVersion,
  isAuthenticated,
  model,
  modelInputSettings,
  modelStatus,
  permissions,
  setPredictionIdInUrl,
  schema,
  token,
  version,
}:
  | VersionAPIPlaygroundProps
  | ModelAPIPlaygroundProps
  | DeploymentAPIPlaygroundProps) {
  const queryClient = useQueryClient();

  // By priming the react-query cache with the initial prediction, we can skip
  // the initial loading state (hence hiding the spinner) and go straight to the
  // prediction detail view.
  //
  // Note that unless we adjust the "staleTime" of the query, the prediction
  // will be re-fetched after the initial render, which will result in another
  // background network request without a UI update.
  if (initialPrediction) {
    queryClient.setQueryData<Prediction>(
      queryKeys.predictions.uuid(initialPrediction.id),
      initialPrediction
    );
  }

  const context: PlaygroundContextValue = {
    elementVisibility: getElementVisibility({
      permissions,
      deployment,
      screen: "api-playground",
    }),
    features,
    hideAdvancedInputs: hideAdvancedInputs ?? false,
    isAuthenticated,
    modelInputSettings: modelInputSettings ?? { hidden: [] },
    modelStatus: modelStatus ?? null,
    permissions,
    renderMode: getRenderMode({ features }),
    token: token ?? null,
    ...(deployment != null
      ? {
          model: deployment.current_release.version._extras.model,
          usesVersionlessApi: false,
          version: deployment.current_release.version,
        }
      : model != null
        ? {
            model: model,
            usesVersionlessApi: true,
            version: version,
          }
        : {
            model: version._extras.model,
            usesVersionlessApi: false,
            version: version,
          }),
  };

  let initialPredictionWrapper: InitialPrediction | null = null;
  if (initialPrediction) {
    if (context.usesVersionlessApi) {
      initialPredictionWrapper = {
        usesVersionlessApi: true,
        prediction: initialPrediction,
        version: initialPredictionVersion,
      };
    } else {
      if (!initialPredictionVersion) {
        throw new Error(
          "Expected initialPredictionVersion prop to be passed to APIPlayground"
        );
      }
      initialPredictionWrapper = {
        usesVersionlessApi: false,
        prediction: initialPrediction,
        version: initialPredictionVersion,
      };
    }
  }

  const playgroundComponent = () => {
    if (deployment) {
      return (
        <DeploymentPlaygroundForm
          initialPrediction={initialPredictionWrapper}
          deployment={deployment}
        />
      );
    }

    if (model) {
      return (
        <ModelPlaygroundForm
          initialPrediction={initialPredictionWrapper}
          setPredictionIdInUrl={
            isAuthenticated && (setPredictionIdInUrl ?? true)
          }
          model={model}
          schema={schema}
        />
      );
    }

    return (
      <VersionPlaygroundForm
        initialPrediction={initialPredictionWrapper}
        setPredictionIdInUrl={isAuthenticated && (setPredictionIdInUrl ?? true)}
        version={version}
      />
    );
  };

  return (
    <PlaygroundContext.Provider value={context}>
      {playgroundComponent()}
    </PlaygroundContext.Provider>
  );
}

function DeploymentPlaygroundForm({
  initialPrediction,
  deployment,
}: {
  initialPrediction: InitialPrediction | null;
  deployment: Deployment;
}) {
  const inputSchema = getInputSchema(
    deployment.current_release.version._extras
  );
  const {
    query: predictionQuery,
    run: handleSubmit,
    state: submissionState,
  } = useCreateDeploymentPrediction({
    inputSchema,
    deployment,
  });

  const form = useInputsForm(
    inputSchema.properties,
    initialPrediction?.prediction.input
  );

  const predictionIsRunning = Boolean(
    predictionQuery.data &&
      !TERMINAL_PREDICTION_STATUSES.includes(predictionQuery.data.status)
  );

  return (
    <>
      <InputOutputGrid
        mobileOutputFirst
        input={
          <PlaygroundFormInput
            form={form}
            isRunning={
              submissionState.status === "pending" || predictionIsRunning
            }
            onSubmit={handleSubmit}
            model={deployment.current_release.version._extras.model}
            version={deployment.current_release.version}
            usesVersionlessAPI={false}
            schema={
              deployment.current_release.version._extras
                .dereferenced_openapi_schema
            }
            // TODO: Update the code snippets to handle predictions
            // against the deployments API
            showLanguageTabs={false}
          />
        }
        outputIsLoading={
          // Show a spinner if the prediction is in a non-terminal state,
          // or if the prediction is being created.
          predictionIsRunning || submissionState.status === "pending"
        }
        output={
          <PlaygroundFormOutput
            form={form}
            initialPrediction={initialPrediction}
            prediction={predictionQuery.data}
            submissionState={submissionState}
            updateStartedAt={new Date(predictionQuery.dataUpdatedAt)}
            updateState={predictionQuery}
          />
        }
      />
      <Toaster position="bottom-right" />
    </>
  );
}

function ModelPlaygroundForm({
  initialPrediction,
  setPredictionIdInUrl,
  model,
  schema,
}: {
  initialPrediction: InitialPrediction | null;
  setPredictionIdInUrl: boolean;
  model: Model;
  schema: OpenapiSchema;
}) {
  const { permissions, modelInputSettings } = usePlaygroundContext();
  const inputSchema = getInputSchema({ dereferenced_openapi_schema: schema });
  const {
    query: predictionQuery,
    run: handleSubmit,
    state: submissionState,
  } = useCreateModelPrediction({
    inputSchema,
    model,
    schema: schema,
    setPredictionIdInUrl,
  });

  const form = useInputsForm(
    inputSchema.properties,
    initialPrediction?.prediction.input
  );

  const predictionIsRunning = Boolean(
    predictionQuery.data &&
      !TERMINAL_PREDICTION_STATUSES.includes(predictionQuery.data.status)
  );

  const canEditFeaturedInputs =
    modelInputSettings && permissions.edit_featured_inputs;

  return (
    <>
      <InputOutputGrid
        mobileOutputFirst
        inputHeadingExtra={
          canEditFeaturedInputs && model ? (
            <FeaturedInputs
              model={model}
              type="prediction"
              schema={inputSchema}
            />
          ) : null
        }
        input={
          <PlaygroundFormInput
            form={form}
            isRunning={
              submissionState.status === "pending" || predictionIsRunning
            }
            usesVersionlessAPI={true}
            onSubmit={handleSubmit}
            model={model}
            schema={schema}
          />
        }
        outputIsLoading={
          // Show a spinner if the prediction is in a non-terminal state,
          // or if the prediction is being created.
          predictionIsRunning || submissionState.status === "pending"
        }
        output={
          <PlaygroundFormOutput
            form={form}
            initialPrediction={initialPrediction}
            prediction={predictionQuery.data}
            submissionState={submissionState}
            updateStartedAt={new Date(predictionQuery.dataUpdatedAt)}
            updateState={predictionQuery}
          />
        }
      />
      <Toaster position="bottom-right" />
    </>
  );
}

function VersionPlaygroundForm({
  initialPrediction,
  setPredictionIdInUrl,
  version,
}: {
  initialPrediction: InitialPrediction | null;
  setPredictionIdInUrl: boolean;
  version: Version;
}) {
  const { permissions, modelInputSettings, model } = usePlaygroundContext();
  const inputSchema = getInputSchema(version._extras);
  const {
    query: predictionQuery,
    run: handleSubmit,
    state: submissionState,
  } = useCreateVersionPrediction({
    version,
    setPredictionIdInUrl,
    inputSchema,
  });

  const form = useInputsForm(
    inputSchema.properties,
    initialPrediction?.prediction.input
  );

  const predictionIsRunning = Boolean(
    predictionQuery.data &&
      !TERMINAL_PREDICTION_STATUSES.includes(predictionQuery.data.status)
  );

  const canEditFeaturedInputs =
    modelInputSettings && permissions.edit_featured_inputs;

  return (
    <>
      <InputOutputGrid
        mobileOutputFirst
        inputHeadingExtra={
          canEditFeaturedInputs && model ? (
            <FeaturedInputs
              model={model}
              type="prediction"
              schema={inputSchema}
            />
          ) : null
        }
        input={
          <PlaygroundFormInput
            form={form}
            isRunning={
              submissionState.status === "pending" || predictionIsRunning
            }
            onSubmit={handleSubmit}
            schema={version._extras.dereferenced_openapi_schema}
            model={version._extras.model}
            version={version}
            usesVersionlessAPI={false}
          />
        }
        outputIsLoading={
          // Show a spinner if the prediction is in a non-terminal state,
          // or if the prediction is being created.
          predictionIsRunning || submissionState.status === "pending"
        }
        output={
          <PlaygroundFormOutput
            form={form}
            initialPrediction={initialPrediction}
            prediction={predictionQuery.data}
            submissionState={submissionState}
            updateStartedAt={new Date(predictionQuery.dataUpdatedAt)}
            updateState={predictionQuery}
          />
        }
      />
      <Toaster position="bottom-right" />
    </>
  );
}

type PlaygroundFormState =
  | {
      error: null;
      status: "idle" | "pending" | "success";
    }
  | {
      error: ErrorWithJSON;
      status: "error";
    };

function useCreateDeploymentPrediction({
  inputSchema,
  deployment,
}: {
  inputSchema: CogInputSchema | null;
  deployment: Deployment;
}): {
  query: UseQueryResult<Prediction, ErrorWithJSON>;
  run: (input: FieldValues) => void;
  state: PlaygroundFormState;
} {
  const mutation = useMutation<
    Awaited<ReturnType<typeof createDeploymentPrediction>>,
    ErrorWithJSON,
    Parameters<typeof createDeploymentPrediction>[0]
  >({
    mutationFn: createDeploymentPrediction,
  });

  // Used to check on the status of the newly created prediction. We want to be
  // able to disable the form while the prediction is in a non-terminal state.
  const query = usePrediction({
    uuid: mutation.data?.id ?? null,
  });

  function run(input: FieldValues) {
    const cleanInput = cleanInputForSubmission(
      input,
      getInputSchema(deployment.current_release.version._extras).properties
    );

    const processedInput = processInputForSubmission(
      cleanInput,
      getInputSchema(deployment.current_release.version._extras).properties
    );

    mutation.mutate({
      input: processedInput,
      inputSchema,
      deployment,
    });
  }

  return {
    query,
    run,
    state: mutation,
  };
}

function useCreateModelPrediction({
  inputSchema,
  model,
  setPredictionIdInUrl,
  schema,
}: {
  inputSchema: CogInputSchema | null;
  model: Model;
  setPredictionIdInUrl: boolean;
  schema: OpenapiSchema;
}): {
  query: UseQueryResult<Prediction, ErrorWithJSON>;
  run: (input: FieldValues) => void;
  state: PlaygroundFormState;
} {
  const mutation = useMutation<
    Awaited<ReturnType<typeof createModelPrediction>>,
    ErrorWithJSON,
    Parameters<typeof createModelPrediction>[0]
  >({
    mutationFn: createModelPrediction,
    onSuccess(data) {
      if (setPredictionIdInUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set("prediction", data.id);
        window.history.pushState({}, "", url.toString());
      }
    },
  });

  // Used to check on the status of the newly created prediction. We want to be
  // able to disable the form while the prediction is in a non-terminal state.
  const query = usePrediction({
    uuid: mutation.data?.id ?? null,
  });

  function run(input: FieldValues) {
    const cleanInput = cleanInputForSubmission(
      input,
      getInputSchema({ dereferenced_openapi_schema: schema }).properties
    );

    const processedInput = processInputForSubmission(
      cleanInput,
      getInputSchema({ dereferenced_openapi_schema: schema }).properties
    );

    // FIXME: Official language models using MLC don't currently support `top_k`
    // parameter.
    const { top_k, ...cleanFilteredInput } = processedInput;

    mutation.mutate({
      input: cleanFilteredInput,
      inputSchema,
      model,
    });
  }

  return {
    query,
    run,
    state: mutation,
  };
}

function useCreateVersionPrediction({
  inputSchema,
  setPredictionIdInUrl,
  version,
}: {
  inputSchema: CogInputSchema | null;
  setPredictionIdInUrl: boolean;
  version: Version;
}): {
  query: UseQueryResult<Prediction, ErrorWithJSON>;
  run: (input: FieldValues) => void;
  state: PlaygroundFormState;
} {
  const mutation = useMutation<
    Awaited<ReturnType<typeof createVersionPrediction>>,
    ErrorWithJSON,
    Parameters<typeof createVersionPrediction>[0]
  >({
    mutationFn: createVersionPrediction,
    onSuccess(data) {
      if (setPredictionIdInUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set("prediction", data.id);
        window.history.pushState({}, "", url.toString());
      }
    },
  });

  // Used to check on the status of the newly created prediction. We want to be
  // able to disable the form while the prediction is in a non-terminal state.
  const query = usePrediction({
    uuid: mutation.data?.id ?? null,
  });

  function run(input: FieldValues) {
    const cleanInput = cleanInputForSubmission(
      input,
      getInputSchema(version._extras).properties
    );

    const processedInput = processInputForSubmission(
      cleanInput,
      getInputSchema(version._extras).properties
    );

    mutation.mutate({
      input: processedInput,
      inputSchema,
      version,
    });
  }

  return {
    query,
    run,
    state: mutation,
  };
}

type BasePlaygroundFormInputProps = {
  form: UseFormReturn;
  isRunning: boolean;
  onSubmit: (input: FieldValues) => void;
  model: Model;
  schema: OpenapiSchema;
  showLanguageTabs?: boolean;
};

type PlaygroundFormInputProps =
  | (BasePlaygroundFormInputProps & {
      usesVersionlessAPI: true;
      version?: never;
    })
  | (BasePlaygroundFormInputProps & {
      usesVersionlessAPI: false;
      version: Version;
    });

function PlaygroundFormInput({
  form,
  isRunning,
  onSubmit,
  model,
  version,
  schema,
  showLanguageTabs = true,
  usesVersionlessAPI,
}: PlaygroundFormInputProps) {
  const {
    isAuthenticated,
    hideAdvancedInputs,
    permissions,
    token,
    modelStatus,
    modelInputSettings,
  } = usePlaygroundContext();
  const dockerImageQuery = usesVersionlessAPI
    ? { isSuccess: false, data: undefined }
    : useDockerImage(version);
  const inputHintsQuery = usesVersionlessAPI
    ? useVersionlessInputHints(model)
    : useVersionedInputHints({ ...model, versionId: version.id });

  const [formSubmitLabel, setFormSubmitLabel] = useState(
    modelStatus === "offline" ? "Boot + Run" : "Run"
  );

  const DEFAULT_TAB = "form";
  const [activeTabId, setActiveTabId] = useQueryParam("input", DEFAULT_TAB);

  const isDisabled = !permissions.run;

  const inputSchema = getInputSchema({ dereferenced_openapi_schema: schema });

  const wrappedOnSubmit = (input: FieldValues) => {
    if (isRunning) {
      return;
    }

    if (modelStatus === "offline") {
      setFormSubmitLabel("Run");
    }
    onSubmit(input);
  };

  /**
   * When switching to the JSON tab, we need to check if there are any files
   * uploaded in the form. If there are, we need to prompt the user to confirm
   * that they want to switch to the JSON tab, as this will discard any files
   * they've uploaded. From the JSON tab they can specify file URLs instead.
   */
  const handleJsonTabChange = () => {
    const formValues = form.getValues();
    const fieldsWithFileValues = Object.keys(formValues).filter((key) => {
      const value = formValues[key];
      return Array.isArray(value)
        ? value.some((v) => {
            if (isObject(v) && "value" in v) {
              return v.value instanceof File;
            }
            return v instanceof File;
          })
        : value instanceof File;
    });

    if (fieldsWithFileValues.length === 0) {
      setActiveTabId("json");
      return;
    }

    const confirmRemoveFiles = window.confirm(
      "Switching to the JSON tab will discard any files you've uploaded. File URLs will be preserved. Are you sure you want to switch?"
    );

    if (confirmRemoveFiles) {
      for (const fieldName of fieldsWithFileValues) {
        form.setValue(fieldName, null);
      }
      setActiveTabId("json");
      return;
    }

    return;
  };

  const textInputOptions: TextInputOptions = {};
  const triggerWord = inputHintsQuery.data?.trigger_word;
  if (triggerWord) {
    textInputOptions.tiptap = {
      wordToHighlight: triggerWord,
    };
  }

  // FIXME: Refactor this away.
  const schemaObject = { dereferenced_openapi_schema: schema };
  return (
    <TabProvider
      defaultActiveId={DEFAULT_TAB}
      selectedId={activeTabId ?? DEFAULT_TAB}
      setSelectedId={(id) => {
        if (id === "json") {
          handleJsonTabChange();
        } else {
          setActiveTabId(id ?? undefined);
        }
      }}
    >
      <FormProvider {...form}>
        <TabList responsive size="sm">
          <Tab id="form">Form</Tab>
          {showLanguageTabs ? (
            <>
              <Tab id="json">JSON</Tab>
              <Tab id="nodejs">Node.js</Tab>
              <Tab id="python">Python</Tab>
              <Tab id="http">HTTP</Tab>
              {dockerImageQuery.isSuccess && (
                <>
                  <Tab id="cog">Cog</Tab>
                  <Tab id="docker">Docker</Tab>
                </>
              )}
            </>
          ) : null}
        </TabList>
        <TabPanel className="py-4" tabId="form">
          <InputForm
            disabled={isDisabled}
            hiddenFields={modelInputSettings?.hidden ?? []}
            hideAdvancedInputs={hideAdvancedInputs}
            onSubmit={wrappedOnSubmit}
            properties={inputSchema.properties}
            required={inputSchema.required ?? []}
            advanced={inputSchema.advanced}
            itemClass="py-2.5"
            textInputOptions={textInputOptions}
          >
            {triggerWord ? (
              <div className="mb-4">
                <Banner
                  condensed
                  severity="info"
                  icon={
                    <LightbulbFilament
                      weight="duotone"
                      className="top-0.5 relative"
                    />
                  }
                  description={
                    <p>
                      The trigger word for this model is{" "}
                      <span className="font-semibold">{triggerWord}</span>. Be
                      sure to include it in your prompt.
                    </p>
                  }
                />
              </div>
            ) : null}
          </InputForm>
        </TabPanel>
        {showLanguageTabs ? (
          <>
            <TabPanel className="py-4" tabId="nodejs">
              <div className="space-y-4">
                <RunWithNPX model={model} />
                <div className="relative">
                  <div
                    className="absolute inset-0 flex items-center"
                    aria-hidden="true"
                  >
                    <div className="w-full border-t border-r8-gray-6" />
                  </div>
                  <div className="relative flex justify-center">
                    <span className="bg-white dark:bg-r8-gray-1 px-2 text-sm text-r8-gray-11">
                      or set up a project from scratch
                    </span>
                  </div>
                </div>
                {version ? (
                  <RunWithAPIPlayground
                    context={RunWithContext.NodeJS}
                    schemaObject={schemaObject}
                    token={token}
                    usesVersionlessApi={false}
                    model={version._extras.model}
                    version={version}
                  />
                ) : (
                  <RunWithAPIPlayground
                    context={RunWithContext.NodeJS}
                    model={model}
                    schemaObject={schemaObject}
                    token={token}
                    usesVersionlessApi
                  />
                )}
              </div>
            </TabPanel>
            {activeTabId === "json" ? (
              <TabPanel className="py-4" tabId="json">
                <JSONEditor schema={inputSchema.properties} />
              </TabPanel>
            ) : null}
            <TabPanel className="py-4" tabId="python">
              {version ? (
                <RunWithAPIPlayground
                  context={RunWithContext.Python}
                  schemaObject={schemaObject}
                  token={token}
                  usesVersionlessApi={false}
                  model={version._extras.model}
                  version={version}
                />
              ) : (
                <RunWithAPIPlayground
                  context={RunWithContext.Python}
                  model={model}
                  schemaObject={schemaObject}
                  token={token}
                  usesVersionlessApi
                />
              )}
            </TabPanel>
            <TabPanel className="py-4" tabId="http">
              {version ? (
                <RunWithAPIPlayground
                  context={RunWithContext.HTTP}
                  schemaObject={schemaObject}
                  token={token}
                  usesVersionlessApi={false}
                  model={version._extras.model}
                  version={version}
                />
              ) : (
                <RunWithAPIPlayground
                  context={RunWithContext.HTTP}
                  model={model}
                  schemaObject={schemaObject}
                  token={token}
                  usesVersionlessApi
                />
              )}
            </TabPanel>
            {dockerImageQuery.isSuccess && dockerImageQuery.data && (
              <>
                <TabPanel className="py-4" tabId="cog">
                  {version ? (
                    <RunWithAPIPlayground
                      context={RunWithContext.Cog}
                      dockerImageName={dockerImageQuery.data.docker_image_name}
                      schemaObject={schemaObject}
                      token={token}
                      usesVersionlessApi={false}
                      model={version._extras.model}
                      version={version}
                    />
                  ) : (
                    <RunWithAPIPlayground
                      context={RunWithContext.Cog}
                      dockerImageName={dockerImageQuery.data.docker_image_name}
                      model={model}
                      schemaObject={schemaObject}
                      token={token}
                      usesVersionlessApi
                    />
                  )}
                </TabPanel>
                <TabPanel className="py-4" tabId="docker">
                  {version ? (
                    <RunWithAPIPlayground
                      context={RunWithContext.Docker}
                      dockerImageName={dockerImageQuery.data.docker_image_name}
                      schemaObject={schemaObject}
                      token={token}
                      usesVersionlessApi={false}
                      model={version._extras.model}
                      version={version}
                    />
                  ) : (
                    <RunWithAPIPlayground
                      context={RunWithContext.Docker}
                      dockerImageName={dockerImageQuery.data.docker_image_name}
                      schemaObject={schemaObject}
                      model={model}
                      token={token}
                      usesVersionlessApi
                    />
                  )}
                </TabPanel>
              </>
            )}
          </>
        ) : null}
        <InputFormFooter
          disabled={isDisabled}
          isAuthenticated={isAuthenticated}
          isLoading={isRunning}
          onReset={form.reset}
          submitLabel={formSubmitLabel}
        />
      </FormProvider>
    </TabProvider>
  );
}

function PlaygroundFormOutput({
  form,
  initialPrediction,
  prediction,
  submissionState,
  updateStartedAt,
  updateState,
}: {
  form: UseFormReturn;
  initialPrediction: InitialPrediction | null;
  prediction: Prediction | undefined;
  submissionState: PlaygroundFormState;
  updateStartedAt: Date;
  updateState: PlaygroundFormState;
}) {
  // Confusingly, there are two "versions" here:
  //
  // 1. The version of the model we're currently looking at.
  // 2. The version of the model that the prediction (if it exists) was created
  //    with.
  //
  // These might not match if we have an initial prediction from a different
  // version of the model (such as when the default example was created on a
  // different version).
  //
  // For the input form, we _always_ want to use the version of the model we're
  // currently looking at. For the output, we want to use the version of the
  // model that the prediction (if it exists) was created with.
  const { features, permissions, usesVersionlessApi, version } =
    usePlaygroundContext();

  return (
    <div className="space-y-4">
      {
        // TODO: Handle cases where we don't have a version (eg pipeline
        // models).
        permissions.debug && version != null ? (
          <SetupFailureCheck versionId={version.id} />
        ) : null
      }

      {form.formState.isSubmitted ? (
        // Once the form has been submitted, we want the output to be that
        // of the prediction that was just created via the webform.
        // This block handles the loading, error, and success states for that,
        // and passes down the running prediction query to the PostSubmitOutput component.
        <>
          {submissionState.status === "error" && (
            <PredictionErrorState
              error={submissionState.error}
              fallback="Failed to run the model."
            />
          )}
          {submissionState.status === "pending" && (
            <PredictionLoadingState>Starting...</PredictionLoadingState>
          )}
          {submissionState.status === "success" && (
            <PostSubmitOutput
              prediction={prediction}
              updateStartedAt={updateStartedAt}
              updateState={updateState}
            />
          )}
        </>
      ) : // Until such point, we want the output to be that of the initial
      // prediction, if it exists, and this component handles the loading,
      // error, and success states as well as refetching logic for the initial
      // prediction if it's not terminal.
      initialPrediction == null ? (
        <InitialOutput />
      ) : usesVersionlessApi || initialPrediction.usesVersionlessApi ? (
        <InitialOutput
          prediction={initialPrediction.prediction}
          usesVersionlessApi
        />
      ) : (
        <InitialOutput
          outputVersion={initialPrediction.version}
          prediction={initialPrediction.prediction}
          usesVersionlessApi={false}
          viewedVersion={version}
        />
      )}
    </div>
  );
}

function PostSubmitOutput({
  prediction,
  updateStartedAt,
  updateState,
}: {
  prediction: Prediction | undefined;
  updateStartedAt: Date;
  updateState: PlaygroundFormState;
}) {
  return (
    <>
      {updateState.status === "pending" && (
        <PredictionLoadingState>Starting...</PredictionLoadingState>
      )}
      {updateState.status === "error" && (
        <PredictionErrorState
          error={updateState.error}
          fallback="Failed to fetch prediction."
        />
      )}
      {updateState.status === "success" && prediction && (
        <PredictionDetailOutput
          isNewPrediction
          prediction={prediction}
          queryStartedAt={updateStartedAt}
        />
      )}
    </>
  );
}
